import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUpRight, faCircleCheck, faRss, faTable } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

export type ListViewMode = "list" | "review" | "feed";

interface ListViewSwitcherProps {
	activeView: ListViewMode;
	onSelectList: () => void;
	onSelectReview: () => void;
	onSelectFeed: () => void;
	showFeed?: boolean;
	showReview?: boolean;
	/** Disable the List button (e.g. the List view needs exactly one list selected). */
	disableList?: boolean;
}

export const ListViewSwitcher: FunctionComponent<ListViewSwitcherProps> = ({
	activeView,
	onSelectList,
	onSelectReview,
	onSelectFeed,
	showFeed = true,
	showReview = true,
	disableList = false,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`list.viewSwitcher.${key}`), [t]);

	const baseButtonClass =
		"flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer";
	const activeClass = "bg-white text-blue-action shadow-sm";
	const inactiveClass = "text-text-meta hover:text-text";

	return (
		<div
			className="inline-flex items-center gap-0.5 p-0.5 bg-inactive-item rounded-[10px]"
			data-testid="list-view-switcher"
		>
			<button
				aria-disabled={disableList}
				className={classNames(
					baseButtonClass,
					activeView === "list" ? activeClass : inactiveClass,
					disableList && "opacity-40 cursor-not-allowed hover:text-text-meta"
				)}
				data-testid="list-view-switcher-list"
				type="button"
				onClick={disableList ? undefined : onSelectList}
			>
				<Icon className="w-3.5 h-3.5" icon={faTable} />
				<span>{ts("list")}</span>
			</button>

			{showReview && (
				<button
					className={classNames(
						baseButtonClass,
						activeView === "review" ? activeClass : inactiveClass
					)}
					data-testid="list-view-switcher-review"
					type="button"
					onClick={onSelectReview}
				>
					<Icon className="w-3.5 h-3.5" icon={faCircleCheck} />
					<span>{ts("review")}</span>
					<Icon className="w-3 h-3 text-text-meta" icon={faArrowUpRight} />
				</button>
			)}

			{showFeed && (
				<button
					className={classNames(
						baseButtonClass,
						activeView === "feed" ? activeClass : inactiveClass
					)}
					data-testid="list-view-switcher-feed"
					type="button"
					onClick={onSelectFeed}
				>
					<Icon className="w-3.5 h-3.5" icon={faRss} />
					<span>{ts("feed")}</span>
					<Icon className="w-3 h-3 text-text-meta" icon={faArrowUpRight} />
				</button>
			)}
		</div>
	);
};
